import { Video, SearchResult, ClipResult, TranscriptSegment } from '@/types';
import { videoApi as mockVideoApi, searchApi as mockSearchApi, clipApi as mockClipApi } from '@/services/api';
import { realVideoApi } from '@/services/realApi';
import { API_CONFIG } from '@/config/api';

// Hybrid API that combines real backend with mock data
export const hybridVideoApi = {
  uploadVideo: async (file: File, supabaseToken?: string): Promise<{ id: string; status: string }> => {
    if (API_CONFIG.USE_REAL_UPLOAD && supabaseToken) {
      console.log('Using real upload API');
      return await realVideoApi.uploadVideo(file, supabaseToken);
    } else {
      console.log('Using mock upload API');
      return await mockVideoApi.uploadVideo(file);
    }
  },
  
  getVideos: async (supabaseToken?: string): Promise<Video[]> => {
    try {
      const videos: Video[] = [];
      
      // Always include mock videos for demo purposes
      const mockVideos = await mockVideoApi.getVideos();
      const dummyVideos = mockVideos.map(video => ({
        ...video,
        isRealVideo: false
      }));
      videos.push(...dummyVideos);
      
      // Add real videos if enabled and token available
      if (API_CONFIG.USE_REAL_VIDEO_LIST && supabaseToken) {
        try {
          console.log('Fetching real videos...');
          const realVideos = await realVideoApi.getVideos(supabaseToken);
          const markedRealVideos = realVideos.map(video => ({
            ...video,
            isRealVideo: true
          }));
          videos.push(...markedRealVideos);
          console.log(`Loaded ${realVideos.length} real videos`);
        } catch (error) {
          console.warn('Failed to load real videos, continuing with mock data:', error);
        }
      }
      
      // Sort by creation date, newest first
      return videos.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } catch (error) {
      console.error('Error in hybrid getVideos:', error);
      // Fallback to mock videos only
      return await mockVideoApi.getVideos();
    }
  },
  
  getVideoById: async (id: string, supabaseToken?: string): Promise<Video | undefined> => {
    // First try to get from mock data
    const mockVideo = await mockVideoApi.getVideoById(id);
    if (mockVideo) {
      return { ...mockVideo, isRealVideo: false };
    }
    
    // If not found in mock and we have real API enabled, try real API
    if (API_CONFIG.USE_REAL_PLAYBACK && supabaseToken) {
      try {
        console.log(`Fetching real video details for ${id}`);
        const realVideo = await realVideoApi.getVideoById(id, supabaseToken);
        if (realVideo) {
          return { ...realVideo, isRealVideo: true };
        }
      } catch (error) {
        console.warn('Failed to fetch real video details:', error);
      }
    }
    
    return undefined;
  },
  
  // Keep existing mock functions for features not yet implemented in real API
  getVideoStatus: mockVideoApi.getVideoStatus,
  getVideoTranscript: mockVideoApi.getVideoTranscript,
  processVideo: mockVideoApi.processVideo,
  reprocessVideo: mockVideoApi.reprocessVideo
};

// Export search and clip APIs as-is (these work with both real and mock data)
export const hybridSearchApi = mockSearchApi;
export const hybridClipApi = mockClipApi;
